import { Link } from 'react-router-dom';
import { useState } from 'react';
import { reviews, stats } from '@/mocks/reviews';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';

const faqQuestions = [
  {
    question: '韩漫漫画网站收费吗？',
    answer: '完全免费！韩漫漫画网站上的所有漫画资源均为免费阅读，无需付费或订阅。我们致力于为中文读者提供优质的免费韩漫阅读体验。',
  },
  {
    question: '网站的漫画多久更新一次？',
    answer: '我们每日更新热门连载漫画的最新章节，确保读者能第一时间追更心仪的作品。日均更新量超过300话，涵盖各种题材。',
  },
  {
    question: '可以在手机上阅读吗？',
    answer: '当然可以！韩漫漫画网站采用响应式设计，完美适配手机、平板和电脑端，无论在哪种设备上都能获得舒适的阅读体验。',
  },
  {
    question: '如何找到我想看的漫画？',
    answer: '您可以通过首页的分类导航按题材浏览，也可以使用搜索功能直接查找。我们还提供热门推荐和最新更新列表，帮助您发现优质漫画。',
  },
  {
    question: '网站有广告吗？',
    answer: '我们注重用户体验，网站界面简洁清爽，不会有过多的广告干扰您的阅读。让您沉浸在纯粹的漫画世界中。',
  },
];

export default function About() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = fd.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      setFormData({ name: '', email: '', message: '' });
      return;
    }
    fd.delete('website_alt');

    try {
      const response = await fetch('http://netkqn.com/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们-韩漫漫画,品牌介绍,用户口碑,联系方式"
        description="了解韩漫漫画的品牌故事与发展历程。作为免费韩漫阅读平台，我们致力于为中文读者提供优质韩国漫画资源。查看用户口碑与联系方式。"
        keywords="关于韩漫漫画,韩漫品牌介绍,韩漫用户评价,韩漫联系方式"
        canonicalUrl="/about"
      />
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebPage" data={{
        name: '关于我们 - 韩漫漫画',
        description: '了解韩漫漫画品牌故事、用户口碑与联系方式',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'http://netkqn.com/' },
            { '@type': 'ListItem', position: 2, name: '关于我们', item: 'http://netkqn.com/about' },
          ],
        },
      }} />
      <SchemaOrg type="FAQPage" data={{
        questions: faqQuestions.map((q) => ({
          '@type': 'Question',
          name: q.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: q.answer,
          },
        })),
      }} />

      <Navbar />
      <main className="pt-20">
        <section className="py-12 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="mx-auto max-w-5xl">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-800 font-medium">关于我们</span>
              </nav>

              <div className="text-center mb-14">
                <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                  品牌故事
                </span>
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                  关于韩漫漫画
                </h1>
                <p className="text-foreground-500 text-base max-w-2xl mx-auto leading-relaxed">
                  韩漫漫画诞生于对韩国漫画的热爱。我们致力于为中文读者打造一个纯净、免费、资源丰富的在线漫画平台。
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
                <div className="bg-background-100 rounded-2xl p-6 md:p-8">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 mb-4">
                    <i className="ri-lightbulb-line text-xl"></i>
                  </div>
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">我们的使命</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    让每一位中文读者都能轻松获取优质的韩国漫画资源。我们相信，好的漫画不应该被语言和地域所限制。通过我们的平台，您可以免费阅读到最新、最热门的韩漫作品。
                  </p>
                </div>
                <div className="bg-background-100 rounded-2xl p-6 md:p-8">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-500 mb-4">
                    <i className="ri-eye-line text-xl"></i>
                  </div>
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">我们的愿景</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    成为中文世界最大的韩国漫画阅读社区。我们希望通过持续优化用户体验、丰富漫画资源和提升翻译质量，为读者创造最佳的在线阅读环境。
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center p-6 bg-background-100 rounded-2xl">
                    <div className="font-heading text-2xl md:text-3xl font-bold text-primary-500">
                      {stat.value}
                    </div>
                    <div className="text-foreground-500 text-sm mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="reputation" className="py-12 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="mx-auto max-w-5xl">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
                  用户口碑
                </h2>
                <p className="mt-3 text-foreground-500 text-sm">听听读者们怎么说</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {reviews.map((review) => (
                  <div key={review.id} className="bg-background-50 rounded-2xl p-6">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-primary-500 text-sm"></i>
                      ))}
                    </div>
                    <blockquote className="text-foreground-700 text-sm leading-relaxed mb-4">
                      &ldquo;{review.content}&rdquo;
                    </blockquote>
                    <div className="flex items-center gap-3">
                      <img
                        src={review.avatar}
                        alt={review.name}
                        className="w-10 h-10 rounded-full object-cover"
                        loading="lazy"
                      />
                      <div>
                        <div className="font-medium text-foreground-950 text-sm">{review.name}</div>
                        <div className="text-foreground-500 text-xs">{review.identity}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="faq" className="py-12 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="mx-auto max-w-3xl">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
                  常见问题
                </h2>
              </div>
              <div className="space-y-4">
                {faqQuestions.map((faq, idx) => (
                  <details key={idx} className="group bg-background-100 rounded-xl overflow-hidden">
                    <summary className="px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-background-200 transition-colors">
                      <span className="font-medium text-foreground-950 text-sm pr-4">{faq.question}</span>
                      <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
                      <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
                    </summary>
                    <div className="px-6 pb-4">
                      <p className="text-foreground-600 text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="py-12 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="mx-auto max-w-2xl">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
                  联系我们
                </h2>
                <p className="text-foreground-500 text-sm">
                  有任何问题或建议？欢迎随时与我们联系
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                <div className="bg-background-50 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 mb-3">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <h4 className="font-medium text-foreground-950 text-sm mb-1">邮箱</h4>
                  <p className="text-foreground-500 text-xs">hello@netkqn.com</p>
                </div>
                <div className="bg-background-50 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-xl bg-accent-100 text-accent-500 mb-3">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <h4 className="font-medium text-foreground-950 text-sm mb-1">微信公众号</h4>
                  <p className="text-foreground-500 text-xs">韩漫漫画</p>
                </div>
                <div className="bg-background-50 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-500 mb-3">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <h4 className="font-medium text-foreground-950 text-sm mb-1">工作时间</h4>
                  <p className="text-foreground-500 text-xs">周一至周五 9:00-18:00</p>
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                className="bg-background-50 rounded-2xl p-6 md:p-8"
               
              >
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full h-11 px-4 bg-background-100 border border-background-300 rounded-xl text-sm text-foreground-950 outline-none focus:border-primary-500 transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full h-11 px-4 bg-background-100 border border-background-300 rounded-xl text-sm text-foreground-950 outline-none focus:border-primary-500 transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 bg-background-100 border border-background-300 rounded-xl text-sm text-foreground-950 outline-none focus:border-primary-500 transition-colors resize-none"
                      required
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />

                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full h-11 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 font-medium text-sm rounded-xl transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'loading' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i> 发送中...
                      </>
                    ) : (
                      <>
                        发送留言 <i className="ri-send-plane-line"></i>
                      </>
                    )}
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600 text-center">感谢您的留言！我们会尽快回复您。</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center">{formError}</p>
                  )}
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}